import { Alert, Box, Snackbar } from '@mui/material';
import { Patient } from '_api/types';
import { PatientRouteMap } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import { TableHeader } from 'components/organisms';
import dayjs from 'dayjs';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { usePatients, useSearchResults, useUser } from 'utils/hooks';

export const FindPatientsManagement = () => {
  const navigate = useNavigateTo();
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const { addTherapistToPatient, searchPatients, deletePatient, isLoading } =
    usePatients();
  const { account } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const filteredData = useMemo<Patient[]>(() => {
    if (searchTerm.length > 0 && searchPatients) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const searchPatientsToFilter =
        (searchTerm: string) =>
        ({ firstName, lastName, id, mrn }: Patient) =>
          `${firstName} ${lastName}`.toLowerCase().includes(searchTerm) ||
          id.toString().includes(searchTerm) ||
          mrn?.toLowerCase().includes(searchTerm);

      const filtered = searchPatients.filter(
        searchPatientsToFilter(lowercasedSearchTerm),
      );

      return filtered;
    } else if (searchPatients) {
      return searchPatients;
    } else {
      return [];
    }
  }, [searchPatients, searchTerm]);

  const toCreatePatient = () => navigate(PatientRouteMap.ADD);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  const handleAddPatient = async (patient: Patient) => {
    if (account) {
      try {
        const results = await addTherapistToPatient(patient, Number(account.id));
        if (results) {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
          }, 1500);
        }
      } catch (err: any) {
        setErrorMessage(err.message);
        setShowError(true);
      }
    }
  };

  const handleArchiveOnClick = (patientId: number) => deletePatient({ patientId });

  const handleViewOnClick = (id: number) => {
    navigate(PatientRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number) => {
    navigate(PatientRouteMap.EDIT, id.toString());
  };

  const patientsTableColumns = useMemo<MRT_ColumnDef<Patient>[]>(
    () => [
      {
        header: 'Alert',
        size: 10,
      },
      {
        header: 'Patient Id',
        accessorFn: (row) => row.id,
        size: 10,
      },
      {
        header: 'MRN',
        accessorFn: (row) => row.mrn,
        size: 10,
      },
      {
        header: 'Child',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      },
      {
        header: 'Therapist',
        accessorFn: (row) => {
          return row.therapists.map(
            (therapist, i) =>
              `${therapist.firstName} ${therapist.lastName}${i < row.therapists.length - 1 ? ', ' : ''}`,
          );
        },
      },
      {
        header: 'Activity',
        accessorFn: (row) => {
          return `${row.lastActivityDate ? dayjs(row.lastActivityDate).format('MM/DD/YYYY') : 'N/A'}`;
        },
      },
      {
        header: 'Actions',
        accessorFn: (data) => (
          <ActionsFlexBox
            addPatientOnClick={() => handleAddPatient(data)}
            archiveOnClick={() => handleArchiveOnClick(data.id)}
            editOnClick={() => handleEditOnClick(data.id)}
            viewOnClick={() => handleViewOnClick(data.id)}
            actionButtons={{
              addPatient: true,
              archiveButton: true,
              editButton: true,
              viewButton: true,
            }}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  const handleSearch = (searchTerm: string) => {
    console.log({ searchTerm });
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          onAddClick={toCreatePatient}
          handleSearch={handleSearch}
          searchPlaceholder={'Search Other Patients'}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={patientsTableColumns}
            data={filteredData}
            positionActionsColumn="last"
            enableSorting={false}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowSuccess(false)}
        open={showSuccess}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled">
          Patient Successfully created
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        open={showError}
      >
        <Alert onClose={() => setShowError(false)} severity="error" variant="filled">
          Failed to add Therapist to Patient.
          <br />
          {errorMessage}
          <br />
          Resolve form issues and try again.
        </Alert>
      </Snackbar>
    </Box>
  );
};
